/**
 * The chapter accent table.
 *
 * The seven ids are a WIRE CONTRACT — they are in `blocks.py` and in every deck
 * revision in Postgres — so they are CONTENT categories, not colour names.
 * `src/styles/tokens.ts` is where each is bound to a brand hue, per theme, and
 * `tone-sky` renders blue because it is the fourth chapter colour, not because
 * anything here is sky-coloured.
 *
 * ⚠️ NEVER derive a class name at runtime. Tailwind's JIT only keeps class names
 * it can see as literal strings, so `bg-tone-${t}/15` and
 * `classes.bg.replace("/15", "/70")` both compile to nothing and render nothing.
 * Hub shipped exactly that bug. Every string below is a literal, and a test
 * asserts each tone's classes contain its own id, which catches the copy-paste
 * slip that a table like this invites.
 *
 * Two tokens per tone, not one. `--tone-x` is the FILL and `--tone-x-ink` is
 * the same tone corrected until it clears 4.5:1 as text on the worst ground of
 * its theme. One base colour cannot do both jobs: Amber is the brand's
 * attention colour AND 1.8:1 on a white slide, so the version you fill a chip
 * with and the version you set a word in are different colours by construction.
 * See docs/BRAND.md.
 */

export type Tone = "brand" | "sky" | "emerald" | "amber" | "rose" | "violet" | "neutral";

export const TONES: readonly Tone[] = ["brand", "sky", "emerald", "amber", "rose", "violet", "neutral"] as const;

export interface ToneClasses {
  /** Accent text. */
  text: string;
  /** Filled chip / badge ground: the tinted surface, not the tone at 15%. */
  bg: string;
  border: string;
  /** The same tinted ground, weaker, for a whole card. */
  soft: string;
  /** Opaque fill for progress bars and build dots. */
  fill: string;
  ring: string;
  /** A CSS value for the inline radial glow. A token, not a class. */
  glow: string;
}

export const TONE: Record<Tone, ToneClasses> = {
  brand: {
    text: "text-tone-brand-ink", bg: "bg-tone-brand-surface", border: "border-tone-brand-border",
    soft: "bg-tone-brand-surface/55", fill: "bg-tone-brand/80", ring: "ring-tone-brand/50",
    glow: "var(--tone-brand-glow)",
  },
  sky: {
    text: "text-tone-sky-ink", bg: "bg-tone-sky-surface", border: "border-tone-sky-border",
    soft: "bg-tone-sky-surface/55", fill: "bg-tone-sky/80", ring: "ring-tone-sky/50",
    glow: "var(--tone-sky-glow)",
  },
  emerald: {
    text: "text-tone-emerald-ink", bg: "bg-tone-emerald-surface", border: "border-tone-emerald-border",
    soft: "bg-tone-emerald-surface/55", fill: "bg-tone-emerald/80", ring: "ring-tone-emerald/50",
    glow: "var(--tone-emerald-glow)",
  },
  amber: {
    text: "text-tone-amber-ink", bg: "bg-tone-amber-surface", border: "border-tone-amber-border",
    soft: "bg-tone-amber-surface/55", fill: "bg-tone-amber/80", ring: "ring-tone-amber/50",
    glow: "var(--tone-amber-glow)",
  },
  rose: {
    text: "text-tone-rose-ink", bg: "bg-tone-rose-surface", border: "border-tone-rose-border",
    soft: "bg-tone-rose-surface/55", fill: "bg-tone-rose/80", ring: "ring-tone-rose/50",
    glow: "var(--tone-rose-glow)",
  },
  violet: {
    text: "text-tone-violet-ink", bg: "bg-tone-violet-surface", border: "border-tone-violet-border",
    soft: "bg-tone-violet-surface/55", fill: "bg-tone-violet/80", ring: "ring-tone-violet/50",
    glow: "var(--tone-violet-glow)",
  },
  neutral: {
    text: "text-tone-neutral-ink", bg: "bg-tone-neutral-surface", border: "border-tone-neutral-border",
    soft: "bg-tone-neutral-surface/55", fill: "bg-tone-neutral/80", ring: "ring-tone-neutral/50",
    glow: "var(--tone-neutral-glow)",
  },
};
